import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import localeENGB from '@angular/common/locales/en-GB';
import localeDECH from '@angular/common/locales/de-CH';
import localeFRCH from '@angular/common/locales/fr-CH';
import localeITCH from '@angular/common/locales/it-CH';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { AppConfigService } from './core/services/app-config.service';
import { LanguageService } from './core/services/language.service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { LoaderInterceptor } from './core/interceptors/loader.interceptor';
import { API_BASE_URL } from './core/services/api-services';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { CustomTranslateHttpLoader } from './core/loaders/custom-translate-http-loader';
import { AssetBrowserLoader } from './core/loaders/asset-browser.loader';
import { AuthenticationInterceptor } from './core/interceptors/authentication.interceptor';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { BrowserErrorHandler } from './core/handlers/browser.error.handler';
import { UniversalStorage } from './core/storages/universal.storage';
import { AssetLoader } from './core/loaders/asset.loader';
import { PagedListLoader } from './core/loaders/paged-list.loader';
import { PagedListBrowserLoader } from './core/loaders/paged-list-browser.loader';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateMatPaginatorIntl } from './core/common/translate-mat-paginator.intl';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { WindowBrowserAccessor } from './core/accessors/window-browser.accessor';
import { WindowAccessor } from './core/accessors/window.accessor';
import { MatFormFieldDefaultOptions, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ObservableLoader } from './core/loaders/observable.loader';
import { ObservableBrowserLoader } from './core/loaders/observable-browser.loader';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PwaService } from './core/services/pwa.service';
import { LocalStorage } from './core/storages/local.storage';
import { LocalBrowserStorage } from './core/storages/local-browser.storage';

// TODO: Add other locales here
registerLocaleData(localeENGB);
registerLocaleData(localeDECH);
registerLocaleData(localeFRCH);
registerLocaleData(localeITCH);

export const initFactory = (appConfigService: AppConfigService, languageService: LanguageService) => {
  // Resolve app-config first because languageservice depends on it
  return () => appConfigService.init()
    .then(value => languageService.init());
};

export const baseUrlFactory = (appConfigService: AppConfigService) => {
  // Resolve app-config first because languageservice depends on it
  return () => appConfigService.getConfig().apiUrl;
};

export function getApiUrl(appConfigService: AppConfigService): string {
  return appConfigService.getConfig().apiUrl;
}

export function getCurrentLanguageCode(languageService: LanguageService): string {
  return languageService.currentLanguageCode;
}

export function customTranslateHttpLoaderFactory(injector: Injector) {
  return new CustomTranslateHttpLoader(injector);
}

export const updatesFactory = (pwaService: PwaService) => {
  return () => {
    pwaService.initPwaPrompt();
  };
};

const matFormFieldDefaultOptions: MatFormFieldDefaultOptions = {
  appearance: 'outline'
};

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    BrowserAnimationsModule,
    SharedModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: customTranslateHttpLoaderFactory,
        deps: [Injector],
      },
    }),
    // Transfer cached http response from server to client and prevent duplicate requests
    TransferHttpCacheModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: false,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  exports: [SharedModule],
  providers: [
    { provide: APP_INITIALIZER, useFactory: initFactory, deps: [AppConfigService, LanguageService], multi: true },
    { provide: API_BASE_URL, useFactory: getApiUrl, deps: [AppConfigService] },
    {
      provide: AssetLoader,
      useClass: AssetBrowserLoader
    },
    { provide: LOCALE_ID, useFactory: getCurrentLanguageCode, deps: [LanguageService] },
    LanguageService, 
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useClass: BrowserErrorHandler
    },
    UniversalStorage,
    {
      provide: PagedListLoader,
      useClass: PagedListBrowserLoader
    },
    {
      provide: MatPaginatorIntl,
      useClass: TranslateMatPaginatorIntl
    },
    {
      provide: WindowAccessor,
      useClass: WindowBrowserAccessor
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: matFormFieldDefaultOptions
    },
    {
      provide: ObservableLoader,
      useClass: ObservableBrowserLoader
    },
    {
      provide: LocalStorage,
      useClass: LocalBrowserStorage
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
